import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroupDirective,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ValidatedCheckboxComponent } from '../validated-checkbox/validated-checkbox.component';

export const atLeastOneRequired: ValidatorFn = (
  c: AbstractControl,
): ValidationErrors | null => {
  const v = c.value;
  return Array.isArray(v) && v.length > 0 ? null : { required: true };
};

export interface CheckboxItem {
  id: string;
  label: string;
}

@Component({
  selector: 'app-validated-checkbox-group',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ValidatedCheckboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './validated-checkbox-group.component.html',
  styleUrl: './validated-checkbox-group.component.scss',
})
export class ValidatedCheckboxGroupComponent implements OnInit {
  groupLabel = input.required<string>();

  items = input.required<ReadonlyArray<CheckboxItem>>();

  formArray = input.required<FormArray<FormControl<boolean>>>();
  controlNamePrefix = input<string>('item-');
  translateItems = input<boolean>(true);

  validationTrainslations = input<{ [key: string]: string }>();
  leaveSpaceForValidation = input<boolean>(false);
  requireAtLeastOne = input<boolean>(false);

  private readonly formGroupDir = inject(FormGroupDirective, {
    optional: true,
  });

  private readonly atLeastOneCheckedValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const fa = control as FormArray<FormControl<boolean>>;
    return fa?.controls?.some((c) => !!c.value)
      ? null
      : { requiredAtLeastOne: true };
  };

  ngOnInit(): void {
    if (this.requireAtLeastOne()) {
      this.formArray().addValidators(this.atLeastOneCheckedValidator);
      this.formArray().updateValueAndValidity({ emitEvent: false });
    }
  }

  hasRequiredValidator(): boolean {
    return this.formArray().controls.some((control) =>
      control.hasValidator(Validators.required),
    );
  }

  errorKeys(): string[] {
    const control = this.formArray();
    return control.errors ? Object.keys(control.errors) : [];
  }

  getErrorMessage(errorKey: string): string {
    return this.validationTrainslations()?.[errorKey] ?? errorKey;
  }

  showError(): boolean {
    const c = this.formArray();
    return c.invalid && (c.touched || !!this.formGroupDir?.submitted);
  }
}
